import { useQuery } from 'react-query';
import { LegalDocsConfigurations } from '@fiverr-private/legal_documents_package';
import { SellerEntityTypes } from '@fiverr-private/upmarket_common';
import { ConfigResponse } from '../types/complianceTypes';
import { useOrganizationPackagesData } from './useOrganizationPackagesData';
import { getSellerComplianceConfigData } from './index';

export const SELLER_CONFIG_QUERY_KEY = 'SELLER_CONFIG_QUERY';

export const useSellerConfigData = (sellerId?: number, autofetch = true) => {
    const initialData: ConfigResponse | undefined = undefined;

    const userSellerDataQuery = useQuery({
        queryKey: [SELLER_CONFIG_QUERY_KEY, sellerId?.toString()],
        queryFn: async (): Promise<ConfigResponse> => getSellerComplianceConfigData({ requeseteeId: sellerId }),
        enabled: autofetch,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: Infinity,
        initialData,
    });

    const sellerConfigs = userSellerDataQuery?.data;

    const isEligibleForBackgroundCheck = sellerConfigs?.backgroundCheckData?.isEligibleForBackgroundCheck ?? true;

    const {
        isPackagesDataLoading,
        backgroundCheckPackageData,
        legalDocumentsPackageData,
        workforceClassificationPackageData,
        refetchPackagesData,
    } = useOrganizationPackagesData(sellerConfigs?.backgroundCheckData?.isUs || false);

    const sellerEntityType = sellerConfigs?.generalConfigs?.sellerEntityType;
    const isAgency = sellerEntityType === SellerEntityTypes.AGENCY;
    const isTeamAccount = sellerEntityType === SellerEntityTypes.TEAM;

    return {
        isLoading: userSellerDataQuery?.isLoading || isPackagesDataLoading,
        sellerConfigs,
        canDownloadBackgroundCheckFile: !!sellerConfigs?.backgroundCheckData?.canDownloadBackgroundCheckFile,
        shouldShowBackgroundCheck: isEligibleForBackgroundCheck && !isAgency,
        shouldShowWorkforceClassification: !isAgency,
        shouldShowLegalDocs: true,
        isEligibleForBackgroundCheck,
        backgroundCheckRequestorUserName: sellerConfigs?.backgroundCheckData?.requestorUserName,
        classificationRequestorUserName: sellerConfigs?.workforceClassificationData?.requestorUserName,
        legalDocsConfigurations: {
            ...(sellerConfigs?.legalDocsData || {}),
            legalDocumentsPackageData,
        } as LegalDocsConfigurations,
        backgroundCheckPackageData,
        legalDocumentsPackageData,
        workforceClassificationPackageData,
        sellerEntityType,
        refetchPackagesData,
        refetchSellerConfigs: () => userSellerDataQuery.refetch({ stale: true }),
        isFree: sellerConfigs?.legalDocsData?.legalDocumentsPackageData?.isFree,
        isTeamAccount,
        isUsSeller: sellerConfigs?.backgroundCheckData?.isUs,
    };
};
